import type { DateKey } from './date';
import { addDays } from './date';
import type { Mark, PrayerId, QualityKey } from './prayer';
import { PRAYERS, normaliseMark } from './prayer';

export interface DayRecord {
  date: DateKey;
  marks: Partial<Record<PrayerId, Mark>>;
}

export function emptyDay(date: DateKey): DayRecord {
  return { date, marks: {} };
}

export function markFor(day: DayRecord | undefined, prayer: PrayerId): Mark {
  const m = day?.marks[prayer];
  return m ? normaliseMark(m) : { prayed: false, onTime: false, jamaah: false, takbirah: false };
}

/**
 * The mark stored for a prayer, or undefined when nothing was recorded at all.
 *
 * `markFor` above answers "what is true of this prayer", and an unrecorded
 * prayer and one recorded as missed both come back as four falses — which is
 * what the screens want. The streak rules want the difference: a prayer
 * nobody has said anything about yet is not the same as one somebody has said
 * they missed.
 */
export function recordFor(day: DayRecord | undefined, prayer: PrayerId): Mark | undefined {
  const m = day?.marks[prayer];
  return m ? normaliseMark(m) : undefined;
}

/** A day counts towards a streak only when ALL FIVE prayers meet the quality. */
export function dayMeets(day: DayRecord | undefined, quality: QualityKey): boolean {
  return PRAYERS.every((p) => markFor(day, p)[quality]);
}

export function countRecorded(day: DayRecord | undefined): number {
  return PRAYERS.filter((p) => markFor(day, p).prayed).length;
}

/** A day and a prayer within it — one position in the Fajr-to-Fajr sequence. */
interface Slot {
  date: DateKey;
  index: number;
}

/** The slot before this one, stepping across the day boundary at Fajr. */
function previousSlot(slot: Slot): Slot {
  return slot.index === 0
    ? { date: addDays(slot.date, -1), index: PRAYERS.length - 1 }
    : { date: slot.date, index: slot.index - 1 };
}

// A run cannot be longer than the history we hold; the guards below are a
// safety net against a corrupt date key producing an unbounded walk.
const MAX_WALK_DAYS = 3660;
const MAX_WALK_PRAYERS = MAX_WALK_DAYS * PRAYERS.length;

/** What one slot has to say about a quality: kept, lost, or nothing yet. */
type SlotState = 'met' | 'missed' | 'pending';

/**
 * Met, missed, or not yet anything — and all the generosity in the streak
 * rules lives here.
 *
 * What decides `pending` is the clock, not whether anything was written down:
 *
 * - A prayer whose time has not come cannot be missed, whatever it holds: it
 *   has not happened, and a blank there is a prayer still hours away rather
 *   than a verdict on one. A day filled in ahead of time still counts, though
 *   — that is a record, and the run reads as the run it is.
 * - The prayer whose time it is NOW is pending only while it stands
 *   unrecorded. Someone opening the app a minute after the adhān has missed
 *   nothing. Someone who has recorded it has said what happened, and the run
 *   answers to that at once rather than reading yesterday's number until the
 *   next adhān moves the day along.
 * - Everything behind that is met or missed, and nothing recorded is a miss.
 */
function slotState(
  days: Map<DateKey, DayRecord>,
  slot: Slot,
  quality: QualityKey,
  today: DateKey,
  currentIndex: number,
): SlotState {
  const record = recordFor(days.get(slot.date), PRAYERS[slot.index]!);
  if (record?.[quality]) return 'met';
  if (slot.date === today) {
    if (slot.index > currentIndex) return 'pending';
    if (slot.index === currentIndex && !record) return 'pending';
  }
  return 'missed';
}

/**
 * Consecutive PRAYERS meeting a quality, counted back from today.
 *
 * Counted in prayers rather than in whole days on purpose: a run measured in
 * days does not move at all until the day is over, so four prayers kept since
 * Fajr showed the same number as none of them. In prayers the streak advances
 * five times a day, which is the thing being kept.
 *
 * The walk starts at today's last slot and steps back, letting `slotState`
 * say which slots are not yet anyone's business. An earlier version carried
 * that in the starting point instead — the later of the current prayer and
 * the last one recorded today, then one step back if it was unmet — and it
 * could only be generous once, at the start. Two things fell out of that: the
 * prayer in progress recorded as a miss read exactly like one not recorded
 * yet, so the number did not move until the next adhān; and an ‘Ishā' filled
 * in early moved the start past Maghrib and ‘Asr, which were then walked as
 * ordinary misses hours before their time.
 */
export function computeStreak(
  days: Map<DateKey, DayRecord>,
  quality: QualityKey,
  today: DateKey,
  current: PrayerId,
): number {
  const currentIndex = PRAYERS.indexOf(current);
  let slot: Slot = { date: today, index: PRAYERS.length - 1 };

  let streak = 0;
  for (let guard = 0; guard < MAX_WALK_PRAYERS; guard += 1) {
    const state = slotState(days, slot, quality, today, currentIndex);
    if (state === 'missed') break;
    if (state === 'met') streak += 1;
    slot = previousSlot(slot);
  }
  return streak;
}

/**
 * The whole days inside a run of prayers.
 *
 * The run divided by five and floored, not a count of complete calendar days:
 * a run that began at ‘Asr has a part-day at each end, and what the number is
 * for is the plain "that is about nine days' worth" beside a figure that moves
 * five times a day.
 */
export function streakDays(prayers: number): number {
  return Math.floor(prayers / PRAYERS.length);
}

export interface StreakSummary {
  prayed: number;
  onTime: number;
  jamaah: number;
  takbirah: number;
}

/**
 * One value per quality, in the four keys' own order.
 *
 * The four are always computed together and never iterated over as strings:
 * `QUALITIES` carries the labels and the segment numbers, which is more than
 * a caller building a record of numbers wants, and `Object.fromEntries` over
 * it loses the four keys from the type.
 */
export function byQuality<T>(value: (quality: QualityKey) => T): Record<QualityKey, T> {
  return {
    prayed: value('prayed'),
    onTime: value('onTime'),
    jamaah: value('jamaah'),
    takbirah: value('takbirah'),
  };
}

export function computeStreaks(
  days: DayRecord[],
  today: DateKey,
  current: PrayerId,
): StreakSummary {
  const index = new Map<DateKey, DayRecord>(days.map((d) => [d.date, d]));
  return byQuality((quality) => computeStreak(index, quality, today, current));
}

/**
 * Consecutive days ONE prayer met ONE quality, ending today.
 *
 * This is the streak that survives a bad Fajr. The four summary streaks above
 * gate on all five prayers, so one missed Fajr takes every one of them to
 * zero — including for the four prayers that were kept. Per prayer, a run of
 * forty Maghribs is still forty the morning after a Fajr is slept through,
 * which is both true and the only version of the number a person can act on.
 *
 * Today does not break it until tomorrow: one prayer a day means the run is
 * only ever waiting on today's, never past it. Waiting, though, is not the
 * same as being told — a prayer whose time is in and whose record fails the
 * quality ends the run today, the same way the quality streaks answer to a
 * record as soon as there is one. For `jamaah` that includes a prayer which
 * WAS prayed but not at the masjid: the record says so, so the run ends.
 */
export function computePrayerStreak(
  days: Map<DateKey, DayRecord>,
  prayer: PrayerId,
  today: DateKey,
  current: PrayerId,
  quality: QualityKey = 'prayed',
): number {
  const record = recordFor(days.get(today), prayer);
  const due = PRAYERS.indexOf(prayer) <= PRAYERS.indexOf(current);
  if (record && !record[quality] && due) return 0;

  let cursor = record?.[quality] ? today : addDays(today, -1);
  let streak = 0;
  for (let guard = 0; guard < MAX_WALK_DAYS; guard += 1) {
    if (!markFor(days.get(cursor), prayer)[quality]) break;
    streak += 1;
    cursor = addDays(cursor, -1);
  }
  return streak;
}

/**
 * The longest run of consecutive days this prayer ever met this quality.
 *
 * The record rather than the run in progress. `computePrayerStreak` above
 * counts back from today and so cannot see a run that ended in Ramaḍān, which
 * is exactly the number a bar wants beside its fill: a run of three says
 * nothing about whether three is a start or a collapse, and "you have held
 * this for forty before" says which.
 *
 * Read in date order rather than array order, and a day the history does not
 * hold breaks a run rather than being stepped over: two runs either side of a
 * month nobody recorded are two runs, and a caller holding its days newest
 * first gets the same answer as one holding them oldest first. Keys sort
 * lexicographically, which for `YYYY-MM-DD` is chronologically.
 */
export function longestPrayerStreak(
  days: DayRecord[],
  prayer: PrayerId,
  quality: QualityKey,
): number {
  const index = new Map<DateKey, DayRecord>(days.map((d) => [d.date, d]));
  const dates = [...index.keys()].sort();

  let best = 0;
  let run = 0;
  let previous: DateKey | undefined;

  for (const date of dates) {
    if (previous !== undefined && date !== addDays(previous, 1)) run = 0;
    previous = date;
    run = markFor(index.get(date), prayer)[quality] ? run + 1 : 0;
    if (run > best) best = run;
  }
  return best;
}

/**
 * Is the oldest day loaded part of a run?
 *
 * What the history loader asks before it stops widening for a record. A run
 * counted back from today has `historyReach` to say how far it needs, but a
 * record can lie anywhere behind it — so the only day it is safe to stop at
 * is one carrying nothing, because no run crosses a blank day. Any quality
 * implies `prayed` (see `normaliseMark`), so one question covers all four.
 */
export function runsToOldest(days: DayRecord[]): boolean {
  if (days.length === 0) return false;
  // The oldest day held, by date rather than by position: the loader builds
  // its history oldest first, but nothing about the question depends on that.
  const oldest = days.reduce((a, b) => (b.date < a.date ? b : a));
  return countRecorded(oldest) > 0;
}

export interface PrayerConsistency {
  prayer: PrayerId;
  /** Consecutive days this prayer met each quality, ending today. */
  runs: Record<QualityKey, number>;
  /** The longest this prayer has ever held each quality — its own record. */
  bests: Record<QualityKey, number>;
}

/**
 * Each prayer on its own, against each of the four.
 *
 * This is the shape the summary streaks cannot show. They all gate on the
 * whole day, so between them they only ever report the worst prayer of the
 * five — and the person who never misses Maghrib but loses Fajr twice a week
 * sees one number for both. Twenty runs say which prayer and which quality,
 * which is the only version of the figure anyone can act on.
 */
export function summarisePrayers(
  days: DayRecord[],
  today: DateKey,
  current: PrayerId,
): PrayerConsistency[] {
  const index = new Map<DateKey, DayRecord>(days.map((d) => [d.date, d]));

  return PRAYERS.map((prayer) => {
    const runs = byQuality((quality) =>
      computePrayerStreak(index, prayer, today, current, quality),
    );
    return {
      prayer,
      runs,
      // The run in progress counts towards the record, and the walk above
      // cannot see all of it: today's prayer may be pending — neither met nor
      // missed — and a run waiting on it is still the run it is.
      bests: byQuality((quality) =>
        Math.max(runs[quality], longestPrayerStreak(days, prayer, quality)),
      ),
    };
  });
}

/**
 * How far back, in days, the longest run in this history reaches.
 *
 * The history loader widens while a run still touches the oldest day it has,
 * because that is the only case where the answer could still grow. Quality
 * streaks come back in prayers, so they are converted here — plus a day for
 * the part-day a run can start in.
 */
export function historyReach(days: DayRecord[], today: DateKey, current: PrayerId): number {
  const index = new Map<DateKey, DayRecord>(days.map((d) => [d.date, d]));
  const streaks = computeStreaks(days, today, current);
  const inPrayers = Math.max(streaks.prayed, streaks.onTime, streaks.jamaah, streaks.takbirah);
  const perPrayer = PRAYERS.map((p) => computePrayerStreak(index, p, today, current));
  return Math.max(Math.ceil(inPrayers / PRAYERS.length) + 1, ...perPrayer);
}
